import { useState, useEffect, useRef } from "react";

import postersData from "../MoviesList/posters.json";

const STATUSES = { PENDING: "pending", OK: "ok", FAILED: "failed" };

function isValidUrl(str) {
  if (!str || str.trim() === "") return false;
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function PosterTile({ id, url, status, onLoad, onError }) {
  const invalid = !isValidUrl(url);

  useEffect(() => {
    if (invalid) onError(id);
  }, []);

  if (invalid) {
    return (
      <div style={{
        background: "var(--color-background-secondary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: 8,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}>
        <div style={{
          height: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fcebeb",
          color: "#a32d2d",
          fontSize: 28,
        }}>✕</div>
        <div style={{ padding: "8px 10px" }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 2 }}>
            ID: {id}
          </div>
          <div style={{
            fontSize: 11,
            color: "#a32d2d",
            background: "#fcebeb",
            borderRadius: 4,
            padding: "2px 6px",
            display: "inline-block",
          }}>
            {url === "" ? "Empty URL" : "Invalid URL"}
          </div>
          {url && (
            <div style={{
              fontSize: 10,
              color: "var(--color-text-secondary)",
              marginTop: 4,
              wordBreak: "break-all",
              fontFamily: "monospace",
            }}>{url}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: "var(--color-background-secondary)",
      border: `0.5px solid ${status === STATUSES.FAILED ? "#f09595" : status === STATUSES.OK ? "#97c459" : "var(--color-border-tertiary)"}`,
      borderRadius: 8,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{ position: "relative", height: 140, background: "var(--color-background-tertiary)" }}>
        {status === STATUSES.PENDING && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            fontSize: 12, color: "var(--color-text-secondary)",
          }}>Loading…</div>
        )}
        {status === STATUSES.FAILED && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 4,
            background: "#fcebeb",
          }}>
            <span style={{ fontSize: 28, color: "#a32d2d" }}>✕</span>
          </div>
        )}
        <img
          src={url}
          alt={`Poster ${id}`}
          onLoad={() => onLoad(id)}
          onError={() => onError(id)}
          style={{
            width: "100%", height: "100%", objectFit: "cover",
            opacity: status === STATUSES.OK ? 1 : 0,
            transition: "opacity 0.2s",
          }}
        />
      </div>
      <div style={{ padding: "8px 10px" }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 2 }}>
          ID: {id}
        </div>
        {status !== STATUSES.PENDING && (
          <span style={{
            fontSize: 11,
            color: status === STATUSES.OK ? "#3b6d11" : "#a32d2d",
            background: status === STATUSES.OK ? "#eaf3de" : "#fcebeb",
            borderRadius: 4,
            padding: "2px 6px",
            display: "inline-block",
          }}>
            {status === STATUSES.OK ? "OK" : "Failed to load"}
          </span>
        )}
        <div style={{
          fontSize: 10,
          color: "var(--color-text-secondary)",
          marginTop: 4,
          wordBreak: "break-all",
          fontFamily: "monospace",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>{url}</div>
      </div>
    </div>
  );
}

export default function PosterChecker() {
const entries = Object.entries(postersData);
  const [statuses, setStatuses] = useState(() => {
    const s = {};
    entries.forEach(([id, url]) => {
      s[id] = isValidUrl(url) ? STATUSES.PENDING : STATUSES.FAILED;
    });
    return s;
  });
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(false);

  const handleLoad = (id) => setStatuses((prev) => ({ ...prev, [id]: STATUSES.OK }));
  const handleError = (id) => setStatuses((prev) => ({ ...prev, [id]: STATUSES.FAILED }));

  const total = entries.length;
  const okCount = Object.values(statuses).filter((s) => s === STATUSES.OK).length;
  const failedCount = Object.values(statuses).filter((s) => s === STATUSES.FAILED).length;
  const pendingCount = Object.values(statuses).filter((s) => s === STATUSES.PENDING).length;

  const failedEntries = entries.filter(([id]) => statuses[id] === STATUSES.FAILED);

  const expiredJson = JSON.stringify(
    Object.fromEntries(failedEntries.map(([id, url]) => [id, url])),
    null, 2
  );

  const copyExpired = () => {
    navigator.clipboard.writeText(expiredJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const filteredEntries = entries.filter(([id]) => {
    if (filter === "all") return true;
    if (filter === "ok") return statuses[id] === STATUSES.OK;
    if (filter === "failed") return statuses[id] === STATUSES.FAILED;
    if (filter === "pending") return statuses[id] === STATUSES.PENDING;
    return true;
  });

  return (
    <div style={{ padding: "1.5rem 1rem", maxWidth: 960, margin: "0 auto", fontFamily: "var(--font-sans)" }}>
      <h2 style={{ fontSize: 20, fontWeight: 500, margin: "0 0 4px", color: "var(--color-text-primary)" }}>
        Poster link checker
      </h2>
      <p style={{ fontSize: 14, color: "var(--color-text-secondary)", margin: "0 0 1.5rem" }}>
        {pendingCount > 0 ? `Checking ${pendingCount} remaining…` : `All ${total} posters checked.`}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: "1.5rem" }}>
        {[
          { label: "Total", value: total, bg: "var(--color-background-secondary)", color: "var(--color-text-primary)" },
          { label: "Loaded OK", value: okCount, bg: "#eaf3de", color: "#3b6d11" },
          { label: "Failed", value: failedCount, bg: "#fcebeb", color: "#a32d2d" },
          { label: "Pending", value: pendingCount, bg: "#faeeda", color: "#854f0b" },
        ].map(({ label, value, bg, color }) => (
          <div key={label} style={{
            background: bg, borderRadius: 8, padding: "12px 14px", textAlign: "center",
          }}>
            <div style={{ fontSize: 22, fontWeight: 500, color }}>{value}</div>
            <div style={{ fontSize: 12, color, opacity: 0.8, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {["all", "ok", "failed", "pending"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "5px 14px",
              fontSize: 13,
              borderRadius: 6,
              border: filter === f ? "1.5px solid var(--color-border-primary)" : "0.5px solid var(--color-border-secondary)",
              background: filter === f ? "var(--color-background-primary)" : "transparent",
              color: "var(--color-text-primary)",
              cursor: "pointer",
              fontWeight: filter === f ? 500 : 400,
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {failedCount > 0 && (
        <div style={{
          border: "0.5px solid #f09595",
          borderRadius: 10,
          padding: "1rem 1.25rem",
          background: "var(--color-background-primary)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 500, margin: 0, color: "#a32d2d" }}>
              Expired / broken posters ({failedCount})
            </h3>
            <button
              onClick={copyExpired}
              style={{
                fontSize: 12,
                padding: "5px 12px",
                borderRadius: 6,
                border: "0.5px solid #f09595",
                background: copied ? "#eaf3de" : "transparent",
                color: copied ? "#3b6d11" : "#a32d2d",
                cursor: "pointer",
              }}
            >
              {copied ? "Copied!" : "Copy JSON"}
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                  <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--color-text-secondary)", fontWeight: 500 }}>ID</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Reason</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", color: "var(--color-text-secondary)", fontWeight: 500 }}>URL</th>
                </tr>
              </thead>
              <tbody>
                {failedEntries.map(([id, url]) => {
                  const reason = !url || url.trim() === ""
                    ? "Empty"
                    : !isValidUrl(url)
                    ? "Invalid URL"
                    : "Load failed";
                  return (
                    <tr key={id} style={{ borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                      <td style={{ padding: "8px 10px", fontFamily: "monospace", fontWeight: 500, color: "var(--color-text-primary)" }}>{id}</td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{
                          fontSize: 11,
                          background: "#fcebeb",
                          color: "#a32d2d",
                          borderRadius: 4,
                          padding: "2px 7px",
                        }}>{reason}</span>
                      </td>
                      <td style={{
                        padding: "8px 10px",
                        fontFamily: "monospace",
                        fontSize: 11,
                        color: "var(--color-text-secondary)",
                        wordBreak: "break-all",
                        maxWidth: 380,
                      }}>{url || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 13, color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" }}>
              View as JSON
            </summary>
            <pre style={{
              marginTop: 10,
              padding: "12px 14px",
              background: "var(--color-background-secondary)",
              borderRadius: 6,
              fontSize: 12,
              fontFamily: "monospace",
              overflowX: "auto",
              color: "var(--color-text-primary)",
            }}>{expiredJson}</pre>
          </details>
        </div>
      )}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: 12,
        marginBottom: "2rem",
      }}>
        {filteredEntries.map(([id, url]) => (
          <PosterTile
            key={id}
            id={id}
            url={url}
            status={statuses[id]}
            onLoad={handleLoad}
            onError={handleError}
          />
        ))}
      </div>
    </div>
  );
}